import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Mail, Phone, MapPin, Send, CheckCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/contact`, formData);
      setSuccess(true);
      setFormData({ name: '', email: '', phone: '', company: '', message: '' });
    } catch (err) {
      setError(t('contact.form.error'));
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      label: 'Telefon',
      value: '0542 223 53 19',
      href: 'tel:+905422235319',
    },
    {
      icon: Phone,
      label: 'Telefon',
      value: '0542 223 61 73',
      href: 'tel:+905422236173',
    },
    {
      icon: Mail,
      label: 'E-posta',
      value: 'info@beudanismanlik.com',
      href: 'mailto:info@beudanismanlik.com',
    },
    {
      icon: MapPin,
      label: 'Adres',
      value: 'Büyükdere Caddesi, Astoria İş Kuleleri No:127 34394 Esentepe, Şişli, İstanbul',
    },
  ];

  return (
    <div data-testid="contact-page" className="overflow-hidden">
      {/* Noise Overlay */}
      <div className="noise-overlay" />

      {/* Hero Section */}
      <section className="relative pt-40 pb-24 md:pb-32 bg-[#0F1A2E]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <p className="text-[#C9A84C] text-sm font-mono tracking-widest uppercase mb-6">
              {t('contact.title')}
            </p>
            <h1 className="font-heading text-5xl md:text-7xl font-bold text-white leading-none mb-8">
              {t('contact.subtitle')}
            </h1>
            <p className="text-white/60 text-lg md:text-xl leading-relaxed">
              {t('contact.description')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-24 md:py-32 bg-[#F8F6F3]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="font-heading text-3xl font-bold text-[#1B2B4B] mb-8">
                {t('footer.contact')}
              </h2>
              <div className="space-y-8 mb-12">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#C9A84C]/10 flex items-center justify-center flex-shrink-0">
                      <info.icon className="text-[#C9A84C]" size={24} />
                    </div>
                    <div>
                      <p className="text-[#1B2B4B]/50 text-sm mb-1">{info.label}</p>
                      {info.href ? (
                        <a href={info.href} className="text-[#1B2B4B] font-medium hover:text-[#C9A84C] transition-colors">{info.value}</a>
                      ) : (
                        <p className="text-[#1B2B4B] font-medium">{info.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Google Maps Embed */}
              <div className="bg-white border border-[#1B2B4B]/10 p-4 shadow-lg">
                <div className="aspect-video overflow-hidden rounded">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3008.8376693890436!2d29.0175!3d41.0769!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14cab63f1e2c2d85%3A0x7d3c7bc8b1e1e5d1!2sAstoria%20%C4%B0%C5%9F%20Kuleleri!5e0!3m2!1str!2str!4v1707656400000!5m2!1str!2str"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="BEU Danışmanlık - Astoria İş Kuleleri, Şişli"
                  ></iframe>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <MapPin className="text-[#C9A84C]" size={16} />
                  <span className="text-[#1B2B4B]/60 text-sm">Astoria İş Kuleleri, Esentepe, Şişli</span>
                </div>
              </div>
            </motion.div>

            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              {success ? (
                <div
                  data-testid="contact-success"
                  className="bg-white p-12 text-center shadow-lg border border-[#C9A84C]/20"
                >
                  <CheckCircle className="text-[#C9A84C] mx-auto mb-6" size={64} />
                  <h3 className="font-heading text-2xl font-bold text-[#1B2B4B] mb-4">
                    {t('contact.form.success')}
                  </h3>
                  <Button
                    onClick={() => setSuccess(false)}
                    data-testid="send-another-btn"
                    className="bg-[#C9A84C] text-white hover:bg-[#b89a43] rounded-none px-8 py-4 mt-4"
                  >
                    Yeni Mesaj Gönder
                  </Button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  data-testid="contact-form"
                  className="bg-white p-8 md:p-12 space-y-6 shadow-lg border border-[#1B2B4B]/10"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-[#1B2B4B]/70">
                        {t('contact.form.name')} *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        data-testid="contact-name-input"
                        className="bg-[#F8F6F3] border-[#1B2B4B]/20 focus:border-[#C9A84C] text-[#1B2B4B] placeholder:text-[#1B2B4B]/30 rounded-none h-12"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-[#1B2B4B]/70">
                        {t('contact.form.email')} *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        data-testid="contact-email-input"
                        className="bg-[#F8F6F3] border-[#1B2B4B]/20 focus:border-[#C9A84C] text-[#1B2B4B] placeholder:text-[#1B2B4B]/30 rounded-none h-12"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-[#1B2B4B]/70">
                        {t('contact.form.phone')}
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        data-testid="contact-phone-input"
                        className="bg-[#F8F6F3] border-[#1B2B4B]/20 focus:border-[#C9A84C] text-[#1B2B4B] placeholder:text-[#1B2B4B]/30 rounded-none h-12"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company" className="text-[#1B2B4B]/70">
                        {t('contact.form.company')}
                      </Label>
                      <Input
                        id="company"
                        name="company"
                        type="text"
                        value={formData.company}
                        onChange={handleChange}
                        data-testid="contact-company-input"
                        className="bg-[#F8F6F3] border-[#1B2B4B]/20 focus:border-[#C9A84C] text-[#1B2B4B] placeholder:text-[#1B2B4B]/30 rounded-none h-12"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-[#1B2B4B]/70">
                      {t('contact.form.message')} *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      rows={6}
                      value={formData.message}
                      onChange={handleChange}
                      data-testid="contact-message-input"
                      className="bg-[#F8F6F3] border-[#1B2B4B]/20 focus:border-[#C9A84C] text-[#1B2B4B] placeholder:text-[#1B2B4B]/30 rounded-none resize-none"
                    />
                  </div>

                  {error && (
                    <p data-testid="contact-error" className="text-red-500 text-sm">
                      {error}
                    </p>
                  )}

                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit-btn"
                    className="w-full bg-[#C9A84C] text-white hover:bg-[#b89a43] rounded-none h-14 text-lg font-semibold tracking-wide disabled:opacity-50"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-3">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Gönderiliyor...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-3">
                        <Send size={20} />
                        {t('contact.form.submit')}
                      </span>
                    )}
                  </Button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};
